import { useState } from "react";
import { Box, Typography, Button, styled } from "@mui/material";
import { useChannelStats } from "@/hooks/use-channel-stats";
import { formatLabel } from "@/utils/format-label";
import ProductCard, {
  ProductCardSkeleton,
} from "./_components/product-card";

const SectionRoot = styled("section")(({ theme }) => ({
  margin: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const Container = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2),
}));

const Grid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
  [theme.breakpoints.up("sm")]: { gridTemplateColumns: "1fr 1fr" },
  [theme.breakpoints.up("lg")]: { gridTemplateColumns: "1fr 1fr 1fr" },
}));

const PeriodSelector = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.08)"
      : theme.palette.grey[100],
  borderRadius: 20,
  padding: 4,
  width: "fit-content",
  gap: 2,
}));

const PeriodButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "isActive",
})(({ theme, isActive }) => ({
  minWidth: 48,
  height: 32,
  borderRadius: 20,
  fontSize: "0.8rem",
  textTransform: "none",
  fontWeight: isActive ? 600 : 400,
  backgroundColor: isActive ? theme.palette.primary.main : "transparent",
  color: isActive ? "#fff" : theme.palette.text.secondary,
  "&:hover": {
    backgroundColor: isActive
      ? theme.palette.primary.main
      : theme.palette.action.hover,
  },
}));

const PERIODS = ["7d", "14d", "30d", "60d", "90d"];

const toProduct = (channel) => ({
  id: channel.channel,
  name: formatLabel(channel.channel),
  status: channel.totalCount > 0 ? "active" : "inactive",
  stats: {
    volume: channel.totalVolume,
    count: channel.totalCount,
    success_rate: channel.totalCount > 0 ? channel.successRate : null,
  },
});

const Products = () => {
  const [period, setPeriod] = useState("30d");
  const { data, isLoading } = useChannelStats(period);

  const products = (data?.channels || []).map(toProduct);
  const activeProducts = products.filter((p) => p.status === "active").length;

  return (
    <SectionRoot>
      <Container>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
             WayaQuick Products
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              {products.length} products · {activeProducts} active
            </Typography>
          </Box>

          <PeriodSelector>
            {PERIODS.map((p) => (
              <PeriodButton
                key={p}
                isActive={period === p}
                onClick={() => setPeriod(p)}
              >
                {p}
              </PeriodButton>
            ))}
          </PeriodSelector>
        </Box>

        <Grid>
          {isLoading
            ? Array.from({ length: 9 }).map((_, i) => (
                <ProductCardSkeleton key={i} />
              ))
            : products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
        </Grid>
      </Container>
    </SectionRoot>
  );
};

export default Products;
